import type { SatelliteWebLandlordPropertySearchResponseDto } from '@zg-rentals/ts-satellite-client';

export type PropertyListingItem = {
  label: string;
  value: string;
  meta?: string;
  /** Street line (street + unit), e.g. "123 Main St #4". Returned separately so callers can render it on its own line. */
  streetAddress?: string;
  /** City/state/zip line, e.g. "Seattle, WA 98101". Returned separately so callers can render it as a muted secondary line. */
  cityStateZip?: string;
};

/**
 * Formats property listings for display in dropdown/combobox components.
 *
 * @param listings - Array of property listings from the API
 * @param isSmallViewport - Whether to format for small viewport (< 480px, Constellation MD breakpoint)
 * @returns Formatted array of property listing items with label, value, and meta
 *
 * @example
 * // Property with name (community):
 * // Small viewport (< 480px): "Sunset Apartments"
 * // Large viewport (≥ 480px): "Sunset Apartments"
 *
 * @example
 * // Property without name, with unit:
 * // Small viewport (< 480px): "123 Main St #4"
 * // Large viewport (≥ 480px): "123 Main St #4 Seattle, WA 98101"
 *
 * @example
 * // Property without name, no unit:
 * // Small viewport (< 480px): "123 Main St"
 * // Large viewport (≥ 480px): "123 Main St Seattle, WA 98101"
 */
export default function formatPropertyListings(
  listings: SatelliteWebLandlordPropertySearchResponseDto['listings'],
  isSmallViewport: boolean,
): Array<PropertyListingItem> {
  // A malformed search page can yield a nullish element even though the DTO types it non-nullable;
  // destructuring one throws inside the caller's render and takes the whole page down.
  return (listings ?? [])
    .filter((listing) => listing != null)
    .map((listing) => {
      const { address, isCommunityTypeListing, listingAlias, name } = listing;
      const { city, state, street, unit, zip } = address ?? {};
      const cityTrimmed = (city ?? '').trim();
      const streetTrimmed = (street ?? '').trim();
      const stateStr = state ?? '';
      const zipStr = zip ?? '';
      const cityStateZip = `${cityTrimmed}, ${stateStr} ${zipStr}`;
      const namePrefix = isCommunityTypeListing && name ? (name ?? '').trim() : '';

      // Build labels efficiently
      const unitPart = unit ? ` #${unit}` : '';
      const streetShort = `${streetTrimmed}${unitPart}`;
      const streetLabel = `${streetShort} ${cityStateZip}`;

      const mobileLabel = namePrefix || streetShort;
      const desktopLabel = namePrefix || streetLabel;
      const displayLabel = isSmallViewport ? mobileLabel : desktopLabel;

      return {
        value: listingAlias || streetLabel,
        label: displayLabel,
        meta: namePrefix ? streetLabel : '',
        streetAddress: streetShort,
        cityStateZip,
      };
    });
}
